import React, { useEffect, useState } from "react";
import Dropdown from "components/dropdown";
import { FiAlignJustify, FiBell } from "react-icons/fi"; // Added FiBell import
import { Link, useNavigate } from "react-router-dom";
import avatars from "assets/img/avatars/avatar4.png";
import AuthService from "../../service/auth.service";
import { usePhotoUrl } from "utils/usePhotoUrl";

const Navbar = ({ onOpenSidenav, brandText }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userName, setUserName] = useState("Madatlas");
  const [avatar, setAvatar] = useState(avatars);
  const { getApiBaseUrl } = usePhotoUrl();
  const [notificationCount, setNotificationCount] = useState(3);
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      message: "Nouvelle demande de contact",
      time: "il y a 5 minutes",
      read: false,
    },
    {
      id: 2,
      message: "Votre projet a été approuvé",
      time: "il y a 2 heures",
      read: false,
    },
    {
      id: 3,
      message: "Rappel: Réunion à 14h00",
      time: "il y a 1 jour",
      read: false,
    },
  ]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profile = await AuthService.getProfile();
        setUserName(`${profile.username}` || "User");
        const fileName = getApiBaseUrl(`${profile.photo}`);
        if (fileName) {
          setAvatar(fileName);
        }
        setAvatar();
      } catch (error) {
        console.error("Failed to fetch profile", error);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    // Update notification count based on unread messages
    setNotificationCount(notifications.filter((n) => !n.read).length);
  }, [notifications]);

  const handleLogout = async () => {
    setLoading(true);
    setError("");

    try {
      await AuthService.logout();
      navigate("auth/sign-in");
    } catch (error) {
      console.error("Logout failed", error);
      setError("Échec de la déconnexion. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = (id) => {
    // Mark the notification as read
    setNotifications(
      notifications.map((n) => (n.id === id ? { ...n, read: true } : n))
    );

    // Navigate to the message details page
    navigate(`/admin/notifications/${id}`);
  };

  // Calculate notification count
  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <nav className="sticky top-4 z-40 flex flex-row flex-wrap items-center justify-between rounded-xl bg-white/10 p-2 backdrop-blur-xl dark:bg-[#0b14374d]">
      <div className="ml-[6px]">
        <div className="h-6 w-[224px] pt-1">
          <a
            className="text-sm font-normal text-navy-700 hover:underline dark:text-white dark:hover:text-white"
            href="/pages"
          >
            Pages
            <span className="mx-1 text-sm text-navy-700 hover:text-navy-700 dark:text-white">
              {" "}
              /{" "}
            </span>
          </a>
          <Link
            className="text-sm font-normal capitalize text-navy-700 hover:underline dark:text-white dark:hover:text-white"
            to="#"
          >
            {brandText}
          </Link>
        </div>
      </div>

      <div className="relative mt-[3px] flex h-[61px] flex-grow items-center justify-around gap-2 rounded-full bg-white px-4 py-2 shadow-xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none md:flex-grow-0 md:gap-1 xl:gap-2">
        <span
          className="flex cursor-pointer text-xl text-gray-600 dark:text-white xl:hidden"
          onClick={onOpenSidenav}
        >
          <FiAlignJustify className="h-5 w-5" />
        </span>
        <div className="flex items-center gap-2">
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            👋 Bonjour, {userName}
          </p>
        </div>

        {/* Notification Icon with Dropdown */}
        <Dropdown
          button={
            <div className="relative cursor-pointer">
              <FiBell className="h-5 w-5 text-gray-600 dark:text-white" />
              {unreadCount > 0 && (
                <div className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                  {unreadCount}
                </div>
              )}
            </div>
          }
          children={
            <div className="flex w-80 flex-col justify-start rounded-[20px] bg-white bg-cover bg-no-repeat shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none">
              <div className="p-4">
                <p className="text-sm font-bold text-navy-700 dark:text-white">
                  Notifications
                  {unreadCount > 0 && (
                    <span className="ml-1 text-xs font-normal text-gray-600 dark:text-gray-400">
                      ({unreadCount} non lue{unreadCount > 1 ? "s" : ""})
                    </span>
                  )}
                </p>
              </div>
              <div className="h-px w-full bg-gray-200 dark:bg-white/20" />

              <div className="flex max-h-[300px] flex-col overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-600 dark:text-gray-400">
                    Aucune notification
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      onClick={() => handleNotificationClick(notif.id)}
                      className={`cursor-pointer border-b border-gray-200 p-4 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-navy-600 ${
                        !notif.read ? "bg-blue-50 dark:bg-navy-900" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p
                            className={`text-sm ${
                              !notif.read ? "font-bold" : ""
                            } text-navy-700 dark:text-white`}
                          >
                            {notif.message}
                          </p>
                          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                            {notif.time}
                          </p>
                        </div>
                        {!notif.read && (
                          <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="h-px w-full bg-gray-200 dark:bg-white/20" />
              <div className="p-2">
                <Link
                  to="admin/messages"
                  className="block w-full rounded-lg p-2 text-center text-sm font-medium text-navy-700 transition-colors hover:bg-gray-100 dark:text-white dark:hover:bg-navy-600"
                >
                  Voir toutes les notifications
                </Link>
              </div>
            </div>
          }
          classNames={"py-2 top-8 -left-[300px] w-max"}
        />

        {/* Profile Dropdown */}
        <Dropdown
          button={
            <img
              className="h-10 w-10 rounded-full"
              src={avatar}
              alt="Profile"
            />
          }
          children={
            <div className="flex w-56 flex-col justify-start rounded-[20px] bg-white bg-cover bg-no-repeat shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none">
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                    {userName}
                  </p>
                </div>
              </div>
              <div className="h-px w-full bg-gray-200 dark:bg-white/20" />
              <div className="flex flex-col p-4">
                <button className="text-left text-sm text-gray-800 dark:text-white hover:dark:text-white">
                  Profile Settings
                </button>
                <button className="mt-3 text-left text-sm text-gray-800 dark:text-white hover:dark:text-white">
                  Newsletter Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="mt-3 text-left text-sm font-medium text-red-500 transition duration-150 ease-out hover:text-red-500 hover:ease-in"
                  disabled={loading}
                >
                  {loading ? "Déconnexion..." : "Log Out"}
                </button>
                {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
              </div>
            </div>
          }
          classNames={"py-2 top-8 -left-[180px] w-max"}
        />
      </div>
    </nav>
  );
};

export default Navbar;
